import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { genElZirvuImage } from "../../../../components/utils/EditorDialogElementGenerator";
import { isIgnoredProblem } from "../../utils/checklistProblem";

// genElZirvuImage is a component (it has hooks), so it is rendered as one
const ZirvuImage = genElZirvuImage;

// Asks the note and the gambar of a problem.
//   mode "check":       the box of a checklist row with a problem is being checked -> onConfirm checks it
//   mode "new-problem": the row is unchecked and is a new problem                 -> onConfirm only keeps note + gambar
//   mode "resolve":     a Foto Kendaraan problem row is being marked solved       -> onConfirm resolves the problem
//   detil: the row (undefined for "resolve", there is no row), problem: the open problem (undefined for "new-problem")
//   onConfirm({ note, gambar }), onHide()
const ChecklistProblemDialog = ({
  visible,
  mode,
  detil,
  problem,
  folderName,
  onConfirm,
  onHide,
}) => {
  const [note, setNote] = useState("");
  const [gambar, setGambar] = useState("");
  const [error, setError] = useState({});

  // start with the note + gambar the row already has (mode "resolve" has no row: starts empty)
  useEffect(() => {
    if (visible) {
      setNote(detil?.note ?? "");
      setGambar(detil?.gambar ?? "");
      setError({});
    }
  }, [visible, detil]);

  const handleOnConfirm = () => {
    const newError = {};
    if (!note || note.trim() === "") newError.note = "Note harus diisi";
    if (!gambar) newError.gambar = "Gambar harus diisi";
    setError(newError);
    if (Object.keys(newError).length > 0) return;

    onConfirm({ note: note.trim(), gambar });
  };

  const isIgnored = problem && isIgnoredProblem(problem);
  const dayCount = problem
    ? Math.floor((new Date() - new Date(problem.tanggal)) / (1000 * 60 * 60 * 24))
    : 0;

  const footerLabel =
    mode === "check"
      ? "Simpan & Centang"
      : mode === "resolve"
      ? "Tandai Selesai"
      : "Simpan";

  return (
    <Dialog
      header={detil?.keterangan || problem?.keterangan || ""}
      visible={visible}
      style={{ width: "30rem" }}
      breakpoints={{ "575px": "95vw" }}
      onHide={onHide}
      footer={
        <>
          <Button
            label="Tutup"
            icon="pi pi-times"
            outlined
            onClick={onHide}
          />
          <Button label={footerLabel} icon="pi pi-check" onClick={handleOnConfirm} />
        </>
      }
    >
      <div className="col-start-start w-full gap-3 pt-2">
        {mode === "check" && isIgnored && (
          <div className="w-full rounded bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-800 row-start-center gap-2">
            <i className="pi pi-exclamation-triangle"></i>
            <span>
              Problem ini berstatus ignore. Yakin ingin mencentang?
            </span>
          </div>
        )}
        {problem && (
          <span className="text-xs italic font-semibold text-slate-400">
            Problem sejak {format(new Date(problem.tanggal), "dd/MM/yyyy")}
            {dayCount > 0 ? ` (${dayCount} hari)` : ""}
          </span>
        )}
        {mode === "check" && (
          <span className="text-sm text-slate-500">
            Isi keterangan kenapa problem ini dicentang.
          </span>
        )}
        {mode === "new-problem" && (
          <span className="text-sm text-slate-500">
            Belum dicentang, jadi akan menjadi problem baru. Isi note dan
            gambar problem ini.
          </span>
        )}
        {mode === "resolve" && (
          <span className="text-sm text-slate-500">
            Isi keterangan kenapa problem ini ditandai selesai.
          </span>
        )}

        <div className="col-start-start w-full gap-1">
          <label className="font-bold text-sm text-slate-500">Note</label>
          <InputTextarea
            className="w-full"
            rows={3}
            autoResize
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {error.note && (
            <small className="font-semibold text-red-400">{error.note}</small>
          )}
        </div>

        <div className="w-full">
          <ZirvuImage
            field="gambar"
            name="Gambar"
            folderName={folderName}
            data={{ gambar }}
            setData={(newData) => {
              const resolved =
                typeof newData === "function" ? newData({ gambar }) : newData;
              setGambar(resolved.gambar ?? "");
            }}
            error={error}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ChecklistProblemDialog;
